// frontend/src/pages/TimeTracking/ManDayReport.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Form, Select, Button, DatePicker, Typography, Row, Col,
    Space, Table, Tag, Divider, Empty, Alert, Skeleton
} from 'antd';
import {
    FileTextOutlined, FileWordOutlined, FilePdfOutlined,
    RollbackOutlined, EyeOutlined, InfoCircleOutlined,
    CheckCircleOutlined, CalendarOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;

const DATE_FORMAT = 'DD/MM/YYYY';

// Ticket types are loaded from /api/ticket-types so dev/prod stay in sync.

const ManDayReport = () => {
    const navigate = useNavigate();
    const notificationApi = useNotificationApi();
    const [form] = Form.useForm();

    // Form watches
    const selectedContract = Form.useWatch('contract_id', form);
    const selectedMonth = Form.useWatch('report_month', form);
    const selectedTicketTypes = Form.useWatch('ticket_types', form);

    // Data
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const [ticketTypes, setTicketTypes] = useState([]);
    const [ticketTypesLoading, setTicketTypesLoading] = useState(false);

    // Preview
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Generated report
    const [reportData, setReportData] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);

    // ------------------------------------------------------------
    // Notifications
    // ------------------------------------------------------------
    const showError = useCallback((title, content, key = 'mandayError') => {
        notificationApi.destroy(key);
        notificationApi.error({
            key, message: title, description: content,
            duration: 4.5, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, content, key = 'mandaySuccess') => {
        notificationApi.destroy(key);
        notificationApi.success({
            key, message: title, description: content,
            duration: 3, placement: 'topRight', style: { width: 420 }
        });
    }, [notificationApi]);

    // ------------------------------------------------------------
    // Load contracts
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchContracts = async () => {
            setContractsLoading(true);
            try {
                const res = await api.get('/api/contracts');
                if (res.data.success) setContracts(res.data.data || []);
            } catch (err) {
                console.error('[ManDayReport] Contracts fetch failed:', err);
                showError('Failed to Load', 'Could not load contracts.');
            } finally {
                setContractsLoading(false);
            }
        };
        fetchContracts();
    }, [showError]);

    // ------------------------------------------------------------
    // Load ticket types (IDs + names from DB)
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchTicketTypes = async () => {
            setTicketTypesLoading(true);
            try {
                const res = await api.get('/api/ticket-types');
                if (res.data.success) setTicketTypes(res.data.data || []);
            } catch (err) {
                console.error('[ManDayReport] Ticket types fetch failed:', err);
            } finally {
                setTicketTypesLoading(false);
            }
        };
        fetchTicketTypes();
    }, []);

    // ------------------------------------------------------------
    // Clear preview/report when form inputs change
    // ------------------------------------------------------------
    useEffect(() => {
        setPreviewData(null);
        setReportData(null);
    }, [selectedContract, selectedMonth, selectedTicketTypes]);

    // ------------------------------------------------------------
    // ALL / multiple selection logic for ticket types
    // ------------------------------------------------------------
    const handleTicketTypesChange = (values) => {
        const lastPicked = values[values.length - 1];
        if (lastPicked === 'ALL') {
            form.setFieldsValue({ ticket_types: ['ALL'] });
            return;
        }
        if (values.includes('ALL') && values.length > 1) {
            form.setFieldsValue({ ticket_types: values.filter(v => v !== 'ALL') });
        }
    };

    // ------------------------------------------------------------
    // Reset
    // ------------------------------------------------------------
    const handleReset = () => {
        form.resetFields();
        setPreviewData(null);
        setReportData(null);
    };

    // ------------------------------------------------------------
    // Generate (fetch preview JSON)
    // ------------------------------------------------------------
    const handleGenerate = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            showError('Missing Fields', 'Please fill in all required fields.');
            return;
        }

        const reportMonth = values.report_month;
        if (!reportMonth) {
            showError('Missing Month', 'Please select a month.');
            return;
        }

        const params = {
            contractId: values.contract_id,
            year: reportMonth.year(),
            month: reportMonth.month() + 1
        };
        if (values.ticket_types?.length) {
            params.ticketTypes = values.ticket_types.join(',');
        }

        setGenerating(true);
        try {
            const res = await api.get('/api/reports/time-tracking/manday/data', { params });
            if (res.data.success) {
                setPreviewData(res.data.data);
                setReportData({ type: 'manday', data: res.data.data });
                showSuccess('Report Ready', 'Man-Days preview generated successfully.');
            } else {
                showError('Generation Failed', res.data.message || 'Failed to generate.');
            }
        } catch (err) {
            console.error('[ManDayReport] Generate failed:', err);
            showError('Generation Failed', err.response?.data?.message || 'An error occurred.');
        } finally {
            setGenerating(false);
        }
    };

    // ------------------------------------------------------------
    // Download (docx/pdf via POST, using server-chosen filename)
    // ------------------------------------------------------------
    const resolveServerFilename = (response, fallback) => {
        try {
            const disposition = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
            if (match) {
                const raw = match[1] || match[2];
                return decodeURIComponent(raw);
            }
        } catch { /* ignore */ }
        return fallback;
    };

    const handleExport = async (selectedFormat) => {
        if (!reportData) return;

        const values = form.getFieldsValue();
        const reportMonth = values.report_month;
        if (!values.contract_id || !reportMonth || !values.ticket_types?.length) {
            showError('Incomplete', 'Please fill in all fields first.');
            return;
        }

        setExporting(true);
        try {
            const res = await api.post('/api/reports/time-tracking/manday', {
                contractId: values.contract_id,
                year: reportMonth.year(),
                month: reportMonth.month() + 1,
                ticketTypes: values.ticket_types,
                format: selectedFormat
            }, { responseType: 'blob' });

            if (res.data instanceof Blob) {
                const fallback = `Man_Days_Report.${selectedFormat}`;
                const filename = resolveServerFilename(res, fallback);

                const url = window.URL.createObjectURL(res.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showSuccess('Download Successful', filename);
            }
        } catch (err) {
            console.error('[ManDayReport] Export failed:', err);
            showError('Export Failed', err.response?.data?.message || 'Could not export.');
        } finally {
            setExporting(false);
        }
    };

    // ------------------------------------------------------------
    // Table columns for a ManDay section
    // ------------------------------------------------------------
    const buildSectionColumns = () => [
        { title: 'BIL.', dataIndex: 'row_number', key: 'row_number', width: 70,
          render: (v) => <Text strong>{v}</Text> },
        { title: 'Ticket UUID', dataIndex: 'ticket_uuid', key: 'ticket_uuid', width: 200,
          render: (v) => v ? <Text code>{v}</Text> : '—' },
        { title: 'Ticket Title', dataIndex: 'ticket_title', key: 'ticket_title', ellipsis: true,
          render: (v) => v || '—' },
        { title: 'Activities', dataIndex: 'activities_text', key: 'activities_text', ellipsis: true,
          render: (v) => {
              if (!v) return '—';
              return (
                  <div style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                      {v}
                  </div>
              );
          } },
        { title: 'Man Hours', dataIndex: 'total_hours', key: 'total_hours', width: 110, align: 'right',
          render: (v) => <Text strong>{parseFloat(v || 0).toFixed(2)}</Text> },
        { title: 'Man Days', dataIndex: 'total_man_days', key: 'total_man_days', width: 110, align: 'right',
          render: (v) => <Text strong>{parseFloat(v || 0).toFixed(2)}</Text> }
    ];

    // ------------------------------------------------------------
    // Section descriptor — which sections to render
    // ------------------------------------------------------------
    const sections = previewData ? [
        {
            key: 'cm',
            title: 'Corrective Maintenance (CM)',
            color: 'green',
            exists: previewData.cm_section_exists,
            entries: previewData.cm_entries,
            totalHours: previewData.cm_total_hours,
            totalManDays: previewData.cm_total_man_days
        },
        {
            key: 'cr',
            title: 'Change Request (CR)',
            color: 'blue',
            exists: previewData.cr_section_exists,
            entries: previewData.cr_entries,
            totalHours: previewData.cr_total_hours,
            totalManDays: previewData.cr_total_man_days
        },
        {
            key: 'pm',
            title: 'Preventive Maintenance (PM)',
            color: 'gold',
            exists: previewData.pm_section_exists,
            entries: previewData.pm_entries,
            totalHours: previewData.pm_total_hours,
            totalManDays: previewData.pm_total_man_days
        },
        {
            key: 'others',
            title: 'Non-SLA Corrective Maintenance (NSLACM)',
            color: 'default',
            exists: previewData.others_section_exists,
            entries: previewData.others_entries,
            totalHours: previewData.others_total_hours,
            totalManDays: previewData.others_total_man_days
        }
    ].filter(s => s.exists && s.entries?.length > 0) : [];

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------
    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">
                    <CalendarOutlined /> Man-Days Service Report
                </Title>
                <Text className="time-tracking-description">
                    Generate a monthly Man-Days Service Report for a contract.
                    Shows CM, CR, PM and Non-SLA Corrective Maintenance sections
                    with hours and man-day totals. Output in DOCX or PDF.
                </Text>
            </div>

            <Form form={form} layout="vertical" name="manday_report_form">
                {/* ============ CARD 1: CONFIGURATION ============ */}
                <Card className="time-tracking-card" bordered={false}>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="contract_id"
                                label="Contract"
                                rules={[{ required: true, message: 'Please select a contract' }]}
                            >
                                <Select
                                    placeholder="Select contract"
                                    showSearch
                                    optionFilterProp="children"
                                    size="large"
                                    loading={contractsLoading}
                                >
                                    {contracts.map(c => (
                                        <Option key={c.id} value={c.id}>
                                            {c.contractName || `Contract #${c.id}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="report_month"
                                label="Month"
                                rules={[{ required: true, message: 'Please select a month' }]}
                            >
                                <DatePicker
                                    picker="month"
                                    format="MMMM YYYY"
                                    style={{ width: '100%' }}
                                    size="large"
                                    placeholder="Select month"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="ticket_types"
                                label="Ticket Type"
                                rules={[{ required: true, message: 'Please select at least one ticket type' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select ticket type(s)"
                                    size="large"
                                    maxTagCount="responsive"
                                    allowClear
                                    loading={ticketTypesLoading}
                                    onChange={handleTicketTypesChange}
                                >
                                    <Option key="ALL" value="ALL">All Types of Ticket</Option>
                                    {ticketTypes.map(t => (
                                        <Option key={t.id} value={t.id}>{t.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Alert
                        message="About Man-Days Service Report"
                        description={
                            <div>
                                <p>This report is based only on <strong>direct time entries</strong> — time logged against a ticket without a CR.</p>
                                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                    <li><strong>CM (Corrective Maintenance)</strong></li>
                                    <li><strong>CR (Change Request)</strong></li>
                                    <li><strong>PM (Preventive Maintenance)</strong></li>
                                    <li><strong>NSLACM (Non-SLA Corrective Maintenance)</strong></li>
                                </ul>
                                <p style={{ marginTop: 8 }}>
                                    <Tag color="cyan">DOCX</Tag>
                                    <Tag color="red">PDF</Tag>
                                    <Tag color="green">Monthly</Tag>
                                    <Tag color="blue">Contract-based</Tag>
                                </p>
                            </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 0 }}
                    />
                </Card>

                {/* ============ ACTIONS BAR ============ */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    marginTop: 16,
                    marginBottom: 8
                }}>
                    <Button size="large" onClick={handleReset} disabled={generating}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        size="large"
                        icon={<EyeOutlined />}
                        loading={generating}
                        onClick={handleGenerate}
                        style={{ background: '#13c2c2', borderColor: '#13c2c2' }}
                    >
                        Generate Report
                    </Button>
                </div>
            </Form>

            {/* ============ CARD 2: REPORT PREVIEW ============ */}
            {reportData && (
                <Card className="time-tracking-card" bordered={false} style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <CheckCircleOutlined style={{ fontSize: 22, color: '#13c2c2' }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>Report Preview</div>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                Review below, then download in your preferred format.
                            </Text>
                        </div>
                        <Space>
                            <Button
                                icon={<FileWordOutlined />}
                                onClick={() => handleExport('docx')}
                                loading={exporting}
                                size="large"
                            >
                                Download DOCX
                            </Button>
                            <Button
                                icon={<FilePdfOutlined />}
                                onClick={() => handleExport('pdf')}
                                loading={exporting}
                                size="large"
                            >
                                Download PDF
                            </Button>
                        </Space>
                    </div>

                    {/* Header info */}
                    <div style={{
                        padding: 16,
                        background: '#f8fafc',
                        borderRadius: 8,
                        marginBottom: 16
                    }}>
                        <Row gutter={[16, 12]}>
                            <Col xs={24} md={8}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Contract</Text>
                                <div><Text strong>{previewData.contract_name}</Text></div>
                            </Col>
                            <Col xs={12} md={4}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Period Start</Text>
                                <div><Text strong>{previewData.period_start}</Text></div>
                            </Col>
                            <Col xs={12} md={4}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Period End</Text>
                                <div><Text strong>{previewData.period_end}</Text></div>
                            </Col>
                            <Col xs={12} md={4}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Grand Total Hours</Text>
                                <div>
                                    <Text strong style={{ color: '#0284c7', fontSize: 16 }}>
                                        {parseFloat(previewData.grand_total_hours || 0).toFixed(2)}
                                    </Text>
                                </div>
                            </Col>
                            <Col xs={12} md={4}>
                                <Text type="secondary" style={{ fontSize: 12 }}>Grand Total Man-Days</Text>
                                <div>
                                    <Text strong style={{ color: '#d97706', fontSize: 16 }}>
                                        {parseFloat(previewData.grand_total_man_days || 0).toFixed(2)}
                                    </Text>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Section tables */}
                    {sections.length === 0 ? (
                        <Empty description="No time entries for the selected period" />
                    ) : (
                        sections.map(section => (
                            <div key={section.key} style={{ marginBottom: 24 }}>
                                <Divider orientation="left">
                                    <Tag color={section.color} style={{ fontSize: 13, padding: '4px 12px' }}>
                                        {section.title}
                                    </Tag>
                                </Divider>

                                <Table
                                    columns={buildSectionColumns()}
                                    dataSource={section.entries}
                                    rowKey={(r, i) => `${section.key}_${i}`}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    scroll={{ x: 'max-content' }}
                                />

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 24,
                                    marginTop: 8,
                                    paddingTop: 8,
                                    borderTop: '1px solid #f0f0f0'
                                }}>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Hours</Text>
                                        <div>
                                            <Text strong style={{ color: '#0284c7', fontSize: 15 }}>
                                                {parseFloat(section.totalHours || 0).toFixed(2)}
                                            </Text>
                                        </div>
                                    </div>
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Man-Days</Text>
                                        <div>
                                            <Text strong style={{ color: '#d97706', fontSize: 15 }}>
                                                {parseFloat(section.totalManDays || 0).toFixed(2)}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <ClockCircleOutlined /> Generated by {previewData.generated_by} · {previewData.generated_date}
                    </Text>
                </Card>
            )}
        </div>
    );
};

export default ManDayReport;